import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { create } from 'zustand';

import { getSession } from '@/auth/AuthSession';
import { isAuthenticated } from '@/auth/AuthUtils';

import { isBiometricPromptInFlight } from './biometricDevice';
import { forgetOtherAccountsEnrollment, getEnrollment } from './biometricEnrollment';

/**
 * Leaving for less than this does not lock. iOS reports "inactive" for Control Center, a notification
 * banner, the Google sign-in sheet, the paywall and Plaid, none of which should cost a Face ID prompt.
 */
export const LOCK_GRACE_MS = 60_000;

export const useAppLock = create<{ locked: boolean }>(() => ({ locked: false }));

let backgroundedAt: number | null = null;

function shouldLock(): boolean {
	const enrollment = getEnrollment();
	return !!enrollment?.lockEnabled && isAuthenticated() && enrollment.userId === getSession()?.userId;
}

export function lockApp(): void {
	useAppLock.setState({ locked: true });
}

export function unlockApp(): void {
	useAppLock.setState({ locked: false });
}

export function handleAppStateChange(isActive: boolean, now: number): void {
	if (!isActive) {
		backgroundedAt = isBiometricPromptInFlight() ? null : now;
		return;
	}

	if (backgroundedAt !== null && now - backgroundedAt >= LOCK_GRACE_MS && shouldLock()) {
		lockApp();
	}
	backgroundedAt = null;
}

/** Locks a cold start straight away, then on every return from a long enough absence. */
export function installAppLock(): void {
	if (!Capacitor.isNativePlatform()) {
		return;
	}

	// Sign-in drops another account's enrollment, but only on the origin that holds it. The session
	// lives in the Keychain and outlives an origin change, so the two can disagree at launch.
	const userId = getSession()?.userId;
	if (userId !== undefined && isAuthenticated()) {
		forgetOtherAccountsEnrollment(userId);
	}

	if (shouldLock()) {
		lockApp();
	}

	void CapacitorApp.addListener('appStateChange', ({ isActive }) => handleAppStateChange(isActive, Date.now())).catch(
		() => undefined,
	);
}
